/**
 * Deep links to a single review.
 *
 * `/#/reviews/:id` is the canonical URL for a review (see
 * `design/architecture.md` § "Frontend routing and deep links"). These helpers
 * are the pure parts of that story, kept out of the components so they can be
 * unit-tested: building the URL, and deciding whether a stashed post-login
 * route may be replayed.
 */

/** In-app route path for a review — what `router.push` takes. */
export function reviewPath(id: number): string {
  return `/reviews/${id}`;
}

/** Full shareable URL for a review, e.g. `http://host:8100/#/reviews/7`. */
export function reviewUrl(origin: string, id: number): string {
  return `${origin}/#${reviewPath(id)}`;
}

/**
 * Decide which route to replay after a successful sign-in.
 *
 * `stored` is whatever the SPA stashed before bouncing an unauthenticated
 * visitor to the login screen; `currentFullPath` is where the OIDC round-trip
 * dropped them (always `/` — the backend never sees the hash fragment).
 *
 * Returns null unless the replay is both wanted and safe:
 *
 * - nothing stashed → nothing to do;
 * - the user didn't land on `/` → they navigated somewhere deliberately, and a
 *   stale entry from an earlier sign-in must not hijack that;
 * - the stash is not a same-origin absolute path (`//evil.example` and
 *   `https://…` are protocol-relative / absolute URLs, not routes) → refuse.
 *   The value comes from `sessionStorage`, so it is only as trustworthy as
 *   anything else running in the tab.
 */
export function pendingRouteToRestore(
  stored: string | null,
  currentFullPath: string,
): string | null {
  if (!stored) return null;
  if (currentFullPath !== "/") return null;
  if (!stored.startsWith("/") || stored.startsWith("//")) return null;
  if (stored === "/") return null;
  return stored;
}
